import styled, { css } from "styled-components";
import { colors, defaultColors, devices, } from "../../../base/styled/variables.styled";
import { typography, fontFamilies, fontWeights, } from "../../../base/styled/typography.style";
const sizeAndPaddingAndTypographyStyles = {
    large: css `
		${typography.buttonLarge}
		padding: 14px 16px;
		height: 56px;
	`,
    medium: css `
		${typography.buttonMedium}
		padding: 12px 16px;
		height: 48px;
	`,
    small: css `
		${typography.buttonSmall}
		padding: 8px 16px;
		height: 40px;
	`,
};
const getPalette = (variant) => variant === "custom" ? defaultColors : colors;
const baseStyles = css `
	font-family: ${fontFamilies.primary};
	font-weight: ${fontWeights.bold};

	border-radius: 8px;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	text-decoration: none;
	gap: 8px;
	border: none;
	transition: all 0.2s;
	position: relative;

	box-sizing: border-box;
	white-space: ${({ allowTextWrap }) => (allowTextWrap ? "normal" : "nowrap")};
	text-align: ${({ allowTextWrap }) => (allowTextWrap ? "center" : "unset")};
	flex-shrink: 0;

	width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
	max-width: 100%;

	&[as="a"] {
		display: inline-flex;
		width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
		max-width: 100%;
		flex-basis: auto;
	}

	@media ${devices.mobile} {
		width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
		display: flex;
		justify-content: center;
		&[as="a"] {
			width: 100%;
			display: flex;
			justify-content: center;
		}
	}

	${({ size }) => sizeAndPaddingAndTypographyStyles[size || "large"]}

	${({ iconOnly, size }) => iconOnly &&
    css `
			padding: 0;
			width: ${size === "large" ? "56px" : size === "medium" ? "48px" : "40px"};
			height: ${size === "large"
        ? "56px"
        : size === "medium"
            ? "48px"
            : "40px"};
			aspect-ratio: 1;
			justify-content: center;
		`}
	&:focus {
		outline-color: ${({ inverse, colorVariant }) => {
    const palette = getPalette(colorVariant);
    return inverse ? palette.brand300 : palette.brand100;
}};
		outline-width: 3px;
		outline-style: solid;
	}
	&:disabled {
		color: ${({ colorVariant }) => getPalette(colorVariant).neutral400};
		cursor: not-allowed;
	}
`;
const variantStyles = {
    primary: css `
		background: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand500 : palette.umbuGreen300;
    }};
		color: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.umbuGreen300 : palette.brand500;
    }};
		border: 2px solid
			${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand500 : palette.umbuGreen300;
    }};
		&:hover:not(:disabled) {
			background: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand600 : palette.umbuGreen400;
    }};
			border-color: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand600 : palette.umbuGreen400;
    }};
		}
		&:focus {
			background: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand600 : palette.umbuGreen400;
    }};
			border-color: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand600 : palette.umbuGreen400;
    }};
		}
		&:disabled {
			background: ${({ colorVariant }) => getPalette(colorVariant).neutral100};
			border-color: ${({ colorVariant }) => getPalette(colorVariant).neutral100};
		}
	`,
    secondary: css `
		background: transparent;
		color: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand500 : palette.umbuGreen300;
    }};
		border: 2px solid
			${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand500 : palette.umbuGreen300;
    }};
		&:hover:not(:disabled) {
			background: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand75 : palette.brand400;
    }};
		}
		&:focus {
			background: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand75 : palette.brand400;
    }};
		}
		&:disabled {
			border-color: ${({ colorVariant }) => getPalette(colorVariant).neutral300};
		}
	`,
    tertiary: css `
		background: transparent;
		color: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand500 : palette.umbuGreen300;
    }};
		border: none;
    &:hover:not(:disabled) {
			background: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand75 : palette.brand400;
    }};
		}
		&:focus {
			background: ${({ inverse, colorVariant }) => {
        const palette = getPalette(colorVariant);
        return !inverse ? palette.brand75 : palette.brand400;
    }};
		}
		&:disabled {
			color: ${({ colorVariant }) => getPalette(colorVariant).neutral300};
		}
  `,
    link: css ``,
    copyButton: css ``
};
export const StyledButton = styled.button.withConfig({
    shouldForwardProp: (prop) => ![
        "variant",
        "inverse",
        "size",
        "iconOnly",
        "fullWidth",
        "showHomeIcon",
        "allowTextWrap",
        "colorVariant",
    ].includes(prop),
}) `
	${baseStyles}
	${({ variant }) => variantStyles[variant]}
`;
export const Icon = styled.span.withConfig({
    shouldForwardProp: (prop) => !['position', 'size'].includes(prop),
}) `
	display: flex;
	align-items: center;
	justify-content: center;

	${({ position }) => position === "left" &&
    css `
			margin-right: 0px;
		`}

	${({ position }) => position === "right" &&
    css `
			margin-left: 0px;
		`}

  svg {
		width: ${({ size }) => (size === "small" ? "20px" : "24px")};
		height: ${({ size }) => (size === "small" ? "20px" : "24px")};
	}
`;
//# sourceMappingURL=Button.style.js.map